/**
 * Estimating a whole PROJECT, which is the unit somebody commissioning work
 * actually thinks in.
 *
 * The pivot quantity is the TOKEN COUNT, not the price. Everything that makes a
 * project heavier or lighter — the agent checking the screen, an existing
 * codebase to carry, a design already provided — acts on tokens; the price grid
 * is applied afterwards, which is what lets one estimate be compared across
 * providers instead of being frozen at the model it happened to run on.
 *
 * This module deliberately does not predict. Leave-one-out on the measured
 * projects says a point estimate is not defensible: on full products, predicting
 * one project from its type-mates is wrong by a factor 4.8, and predicting
 * cost-per-request instead does not rescue it (x5.4), because one of the three
 * did most of its work through subagents. Three projects cannot carry a model.
 *
 * So the answer is a REFERENCE CLASS: here is what projects of this shape really
 * cost, and here is the one closest to what you describe. The "ampleur" control
 * positions the answer inside that measured range rather than multiplying it by
 * a coefficient nobody measured — and the range tightens on its own as projects
 * are added, instead of needing a formula to be defended.
 */
import { consumptionOf, scaleBundle } from './consumption'
import { costOf, costWithoutCache } from './pricing'
import { DERIVED_TYPES, DOCUMENTATION_SHARE, EUR_PER_USD, TAILLE_PARTS } from './config'
import { t } from '../ui/lang'
import type { Library, ModelPrice, TokenBundle } from './types'

export interface MeasuredProject {
  name: string
  type: string
  signals: string[]
  episodes: number
  api_calls: number
  cost_usd: number
  worked_hours: number
  span_days: number | null
  families: Record<string, number>
  visual_share_of_requests: number
  visual_cost_share: number
  tokens: { in: number; out: number; cw: number; cr: number }
}

export interface ProjectType {
  label_fr: string
  label_en: string
  description_fr: string
  description_en: string
  n_projects: number
  projects: string[]
  cost_usd: { min: number; median: number; max: number }
  episodes_median: number
  worked_hours_median: number
  span_days_median: number
  visual_cost_share_median: number
  loo_worst_ratio: number | null
  predictable: boolean
  note_fr: string
}

export interface ProjectLibrary {
  n_projects: number
  types: Record<string, ProjectType>
  projects: MeasuredProject[]
  caveats_fr: string[]
}

/**
 * Things that make a project heavier or lighter, each carrying where its number
 * comes from. Three origins, and the page must not blur them:
 *
 *  - `observed` — mesuré sur le corpus, le facteur vient des données ;
 *  - `owner` — l'ordre de grandeur d'Armand, qui construit ces projets. Rien dans le
 *    corpus n'isole ce levier, mais il pèse trop pour être laissé à 1 : un projet
 *    d'entreprise chiffré comme un projet perso ment plus qu'il n'approxime ;
 *  - `declared` — personne n'a de chiffre. Le facteur reste à 1 et le réglage ne
 *    s'affiche pas : un interrupteur qui ne bouge rien apprend au lecteur qu'aucun
 *    interrupteur ne fait quoi que ce soit.
 */
export interface Modifier {
  id: string
  label_fr: string
  hint_fr: string
  factor: number
  provenance: 'observed' | 'owner' | 'declared'
  evidence_fr: string
}

/** Tous les leviers, dans la langue affichée. */
export function allModifiersOf(): Modifier[] {
  return allModifiers()
}

/** Ceux que la page propose : ceux dont quelqu'un assume le chiffre. */
export function shownModifiers(): Modifier[] {
  return allModifiers().filter((modifier) => modifier.provenance !== 'declared')
}

function allModifiers(): Modifier[] {
  return [
  {
    id: 'visual',
    label_fr: t('L’agent vérifie le rendu à l’écran', 'The agent checks what it looks like'),
    hint_fr: t('Il ouvre la page ou le simulateur, regarde, et corrige ce qu’il voit.',
      'It opens the page or the simulator, looks, and fixes what it sees.'),
    factor: 2.0,
    provenance: 'observed',
    evidence_fr: 'Mesuré sur les 8 projets : la boucle de vérification visuelle pèse '
               + 'de 32 % à 68 % de la facture, médiane ~50 %. C’est le seul réglage '
               + 'qui divise le coût par deux.',
  },
  {
    id: 'existing',
    label_fr: t('Le projet existe déjà', 'The project already exists'),
    hint_fr: t('Il ajoute à une base de code en place, plutôt que de partir de zéro.',
      'It adds to a code base already in place, rather than starting from nothing.'),
    factor: 1.37,
    provenance: 'observed',
    evidence_fr: 'Contre-intuitif mais mesuré sur 6 projets : le premier cinquième '
               + 'des demandes coûte ×0,73 le reste. Démarrer est moins cher — il n’y '
               + 'a presque rien à lire. C’est le code déjà là qu’il faut porter à '
               + 'chaque tour.',
  },
  {
    id: 'retries',
    label_fr: t('Reprises et changements d’avis', 'Redoing and changing your mind'),
    hint_fr: t('Refaire parce que le résultat ne convenait pas.',
      'Doing it again because the result would not do.'),
    factor: 1.0,
    provenance: 'declared',
    evidence_fr: 'NON mesuré. Les erreurs d’API le sont (×1,002, négligeable), mais '
               + 'pas le fait de redemander parce que le résultat ne plaisait pas. '
               + 'À vous de le fixer — je ne l’invente pas.',
  },
  {
    id: 'design',
    label_fr: t('Un design est fourni', 'A design is provided'),
    hint_fr: t('Maquettes prêtes — attention, ça coûte plus cher, pas moins.',
      'Mockups ready — careful, that costs more, not less.'),
    factor: 1.63,
    provenance: 'observed',
    evidence_fr: 'Mesuré, et à rebours de l’intuition : fournir une maquette a '
               + 'coûté ×1,63 (12,36 $ contre 7,56 $). Le design ne fait gagner '
               + 'aucun tour — 146 appels contre 145, même volume écrit, même '
               + 'durée. Il s’AJOUTE à ce qu’il faut relire : la remise pesait '
               + '8 127 lignes, soit 132 000 jetons de plus portés à chaque tour, '
               + 'et la relecture fait 98 % de la facture. Ce qu’elle achète est '
               + 'la cohérence, pas la vitesse : à l’évaluation, 46/50 contre '
               + '42/50, tout l’écart étant sur le contenu et la finition — zéro '
               + 'sur « est-ce que ça marche ». UNE seule paire contrôlée : c’est '
               + 'une direction solide et un coefficient fragile, et l’ampleur '
               + 'dépend directement du poids de la remise.',
  },
  {
    id: 'documented',
    label_fr: t('Le travail doit être documenté par l’IA',
      'The work must be documented by the AI'),
    hint_fr: t('La documentation s’écrit aussi, et elle se paie en jetons.',
      'Documentation gets written too, and it is paid for in tokens.'),
    factor: 1 + DOCUMENTATION_SHARE,
    provenance: 'owner',
    evidence_fr: 'Déclaré, pas mesuré. Un quart de jetons en plus pour la rédaction, et '
               + 'un quart de la note qui passe sur l’indice généraliste : écrire une '
               + 'documentation n’est pas coder, et les modèles ne s’y classent pas '
               + 'pareil.',
  },
  {
    id: 'enterprise',
    label_fr: t('Des besoins d’entreprise, complexes et spécifiques',
      'Enterprise needs, complex and specific'),
    hint_fr: t('Intégrations, conformité, cas particuliers imposés.',
      'Integrations, compliance, special cases you do not choose.'),
    factor: 2.0,
    provenance: 'owner',
    evidence_fr: 'Déclaré, pas mesuré. Le corpus est fait de projets menés par une '
               + 'personne pour elle-même : rien dedans n’isole ce que coûte un '
               + 'existant d’entreprise. Le facteur 2 est l’ordre de grandeur '
               + 'd’Armand, qui construit ces projets.',
  },
  {
    id: 'wide_scope',
    label_fr: t('Un périmètre très large, beaucoup de fonctionnalités différentes',
      'A very wide scope, many different features'),
    hint_fr: t('Beaucoup de domaines différents à faire fonctionner ensemble.',
      'Many different areas to make work together.'),
    factor: 2.0,
    provenance: 'owner',
    evidence_fr: 'Déclaré, pas mesuré. La taille du projet couvre déjà la part de '
               + 'fonctionnalités demandée ; ce réglage-ci dit qu’elles ne se '
               + 'ressemblent pas entre elles, ce que le corpus ne sépare pas. '
               + 'Facteur 2, ordre de grandeur d’Armand.',
  },
  ]
}

export interface ProjectInputs {
  typeId: string
  /** 0 = the smallest measured project of this type, 1 = the largest. */
  ampleur: number
  modifiers: Record<string, boolean | number>
  modelId: string
  /** A model with no prompt caching changes the bill by about x8. */
  cacheActive: boolean
  /** The user's own estimate, in person-days. We never guess it. */
  humanDays: number | null
  dailyRateUsd: number
}

export interface AppliedModifier extends Modifier {
  active: boolean
  effect: number
}

export interface ProjectEstimate {
  type: ProjectType
  model: ModelPrice
  /** The pivot: what the work is expected to cost in tokens, before any price. */
  tokens: TokenBundle
  costUsd: number
  /** Bounds of the measured class, same modifiers applied. */
  rangeUsd: [number, number]
  noCacheUsd: number
  /** The real project sitting closest to the position asked for. */
  reference: MeasuredProject
  workedHours: number
  requests: number
  appliedModifiers: AppliedModifier[]
  humanCostUsd: number | null
  savingUsd: number | null
  ratioHumanOverAgent: number | null
  breakEvenDays: number | null
  warnings: string[]
}

export const DEFAULT_INPUTS: ProjectInputs = {
  typeId: 'site_vitrine',
  ampleur: 0.5,
  modifiers: {
    visual: true, existing: false, retries: 1, design: false,
    documented: false, enterprise: false, wide_scope: false,
  },
  modelId: 'anthropic/claude-sonnet-5',
  cacheActive: true,
  humanDays: null,
  // Stored in USD like everything else, but derived from a round 500 EUR so the
  // field does not greet a French reader with "501 €".
  dailyRateUsd: 500 / EUR_PER_USD,
}

function bundleOf(project: MeasuredProject, scale = 1): TokenBundle {
  return {
    in: project.tokens.in * scale,
    out: project.tokens.out * scale,
    cw: project.tokens.cw * scale,
    cr: project.tokens.cr * scale,
    // The TTL split is not kept per project; charging every write at the
    // published price is the conservative side of a 7.5% effect.
    cw_5m: project.tokens.cw * scale,
    cw_1h: 0,
    turns: Math.round(project.api_calls * scale),
  }
}

/** Geometric interpolation: these costs span orders of magnitude, so the midpoint
 *  between a 97 $ and a 1 932 $ project is not 1 014 $. */

export function projectsOfType(library: ProjectLibrary, typeId: string): MeasuredProject[] {
  return library.projects.filter((project) => project.type === typeId)
}

export function getProjectLibrary(library: Library): ProjectLibrary | null {
  const candidate = library.projects as ProjectLibrary | undefined
  return candidate?.types && candidate.projects ? candidate : null
}

/** Multiplier applied to the measured token count, and why. */
export function applyModifiers(
  inputs: ProjectInputs,
  visualShare: number,
): { scale: number; applied: AppliedModifier[] } {
  const applied: AppliedModifier[] = []
  let scale = 1

  for (const modifier of allModifiers()) {
    const raw = inputs.modifiers[modifier.id]
    if (modifier.id === 'visual') {
      // Measured as a SHARE of each project, so it is removed at the share the
      // corpus actually shows rather than at a round number.
      const active = raw !== false
      const effect = active ? 1 : 1 - visualShare
      scale *= effect
      applied.push({ ...modifier, active, effect })
      continue
    }
    if (typeof raw === 'number') {
      const effect = Math.max(0.1, raw)
      scale *= effect
      applied.push({ ...modifier, active: effect !== 1, effect })
      continue
    }
    const active = raw === true
    const effect = active ? modifier.factor : 1
    scale *= effect
    applied.push({ ...modifier, active, effect })
  }
  return { scale, applied }
}

export function estimateProject(
  inputs: ProjectInputs,
  library: Library,
): ProjectEstimate | null {
  const projectLibrary = getProjectLibrary(library)
  // A derived type is priced on the type it was measured from, cut to its declared share.
  const derived = DERIVED_TYPES[inputs.typeId]
  const baseId = derived?.base ?? inputs.typeId
  const type = projectLibrary?.types?.[baseId]
  const model = library.pricing.models.find((m) => m.id === inputs.modelId)
  if (!projectLibrary || !type || !model) return null

  const members = projectsOfType(projectLibrary, baseId)
  if (members.length === 0) return null

  const { scale, applied } = applyModifiers(inputs, type.visual_cost_share_median)
  // Every dollar below is at the volume THIS model spends, warnings included: a range
  // quoted at the Claude volume beside a headline at the model's own reads as a contradiction.
  const consumption = consumptionOf(model.id)
  const own = (bundle: TokenBundle) => scaleBundle(bundle, consumption.factor)
  const price = (project: MeasuredProject): number => {
    const bundle = own(bundleOf(project, scale))
    return inputs.cacheActive ? costOf(bundle, model) : costWithoutCache(bundle, model)
  }

  const priced = members.map((project) => ({ project, cost: price(project) }))
    .sort((a, b) => a.cost - b.cost)
  const low = priced[0]
  const high = priced[priced.length - 1]
  if (!low || !high) return null

  // La taille est une PART des fonctionnalités du projet de référence — le plus
  // gros mesuré de ce type — et non une position entre deux projets du corpus.
  //
  // Deux raisons de l'avoir changé. Un lecteur comprend « un quart des
  // fonctionnalités d'un projet comme celui-là » ; il ne comprend pas « placé
  // dans la plage mesurée ». Et l'ancienne échelle s'écroulait quand un seul
  // projet d'un type avait été mesuré : les trois tailles donnaient le même
  // chiffre, ce qui apprenait au lecteur que le réglage ne sert à rien.
  // Un type dérivé est une PART d'un type mesuré (config.DERIVED_TYPES) : elle entre ici,
  // au même endroit que la taille, pour que le coût, les jetons, les heures et le nombre de
  // demandes la suivent ensemble.
  const part = (TAILLE_PARTS[Math.round(inputs.ampleur * 2)] ?? 1) * (derived?.share ?? 1)
  const costUsd = high.cost * part
  // Name something that actually happened rather than a number nobody ever paid.
  const reference = priced.reduce((best, entry) =>
    Math.abs(entry.cost - costUsd) < Math.abs(best.cost - costUsd) ? entry : best, low)

  const tokenScale = reference.project.tokens.cr > 0
    ? (costUsd / (reference.cost || 1)) * scale
    : scale
  // The corpus shape, at the Claude volume: each model's own volume is applied where it is
  // priced (above, and per model in catalog.quoteModels), never baked into this bundle.
  const tokens = bundleOf(reference.project, tokenScale)

  // Le temps machine et le nombre de demandes suivent la même part : c'est le
  // travail qui est réduit, pas seulement la facture.
  const hours = (high.project.worked_hours || 0.1) * part * scale
  const requests = Math.round(high.project.episodes * part)

  // Everything here is USD, because everything measured is USD. Converting for a
  // reader is a display concern and lives in the formatter -- an engine that
  // knows about euros would have to know about the rate, and then about when the
  // rate changed.
  const humanCostUsd = inputs.humanDays && inputs.humanDays > 0
    ? inputs.humanDays * inputs.dailyRateUsd
    : null
  const savingUsd = humanCostUsd === null ? null : humanCostUsd - costUsd
  const ratio = humanCostUsd === null || costUsd <= 0 ? null : humanCostUsd / costUsd
  const breakEvenDays = inputs.dailyRateUsd > 0 ? costUsd / inputs.dailyRateUsd : null

  const warnings: string[] = []
  if (!type.predictable) {
    warnings.push(
      `Les projets de ce type vont de ${Math.round(low.cost)} $ à `
      + `${Math.round(high.cost)} $ selon leur contenu : le type de projet ne suffit pas à `
      + `fixer le prix, le vôtre peut tomber n'importe où dans cette plage.`)
  }
  if (type.n_projects < 3) {
    warnings.push(
      'Sur ce type de projet, ce chiffre est un repère, pas une statistique.')
  }
  const guessed = applied.filter((m) => m.active && m.provenance !== 'observed')
  if (guessed.length > 0) {
    const names = guessed.map((m) => `« ${m.label_fr.toLowerCase()} »`).join(', ')
    const many = guessed.length > 1
    warnings.push(
      `${many ? 'Des réglages activés ne sont pas mesurés' : 'Un réglage activé n’est '
        + 'pas mesuré'} : ${names}. ${many ? 'Leur' : 'Son'} effet est un ordre de `
      + 'grandeur déclaré, pas un chiffre issu des données.')
  }
  if (model.provider !== 'anthropic') {
    warnings.push(
      'Le prix de ce modèle est un re-chiffrage du même travail, à sa propre '
      + `consommation. ${consumption.note_fr}`)
  }
  warnings.push(
    'Ces montants viennent de projets réellement construits. Ils répondent à « combien '
    + 'ça a coûté », pas à « combien ça coûtera chez vous ».')

  return {
    type,
    model,
    tokens,
    costUsd,
    // La dispersion mesurée du TYPE, ramenée à la part demandée : la fourchette
    // doit encadrer le chiffre affiché, pas décrire des projets d'une autre
    // taille. Quand un seul projet a été mesuré, low et high sont le même et la
    // fourchette se réduit à un point — ce qui est la vérité.
    rangeUsd: [low.cost * part, high.cost * part],
    noCacheUsd: costWithoutCache(own(bundleOf(reference.project, tokenScale)), model),
    reference: reference.project,
    workedHours: hours,
    requests,
    appliedModifiers: applied,
    humanCostUsd,
    savingUsd,
    ratioHumanOverAgent: ratio,
    breakEvenDays,
    warnings,
  }
}
